import type { SurfaceSolutionTag } from "../api/types";

export type MergeTagIdsOptions = {
  /** Selected temporal phase tag; replaces any existing temporal ids. */
  temporalTagId?: number | null;
  /** All temporal tag ids for the board (so we can strip the old phase). */
  temporalTagIds?: number[] | null;
  /** Selected content-role tag ids; replaces any existing content-role ids. */
  contentRoleIds?: number[] | null;
  /** All content-role tag ids for the board (so we can strip old roles). */
  contentRoleTagIds?: number[] | null;
};

/** Solution catalog ids (`is_solution !== false`). */
export function solutionTagIdSet(
  tags: Array<{ id: number; is_solution?: boolean }> | null | undefined
): Set<number> {
  return new Set(
    (tags ?? []).filter((t) => t.is_solution !== false).map((t) => t.id)
  );
}

/**
 * Build accept `tag_ids`: keep unrelated tags, replace solution / temporal / content-role ids.
 */
export function mergeTagIds(
  existingTagIds: number[] | null | undefined,
  selectedSolutionIds: number[] | null | undefined,
  allTags: SurfaceSolutionTag[] | null | undefined,
  options?: MergeTagIdsOptions
): number[] {
  const existing = existingTagIds ?? [];
  const selected = selectedSolutionIds ?? [];
  const tags = allTags ?? [];

  const solutionIdSet = solutionTagIdSet(tags);
  const temporalIdSet = new Set(
    (options?.temporalTagIds ?? []).filter((id) => Number.isFinite(id))
  );
  const contentRoleIdSet = new Set(
    (options?.contentRoleTagIds ?? []).filter((id) => Number.isFinite(id))
  );

  const preserved = existing.filter(
    (id) =>
      !solutionIdSet.has(id) && !temporalIdSet.has(id) && !contentRoleIdSet.has(id)
  );
  const uniqueSelected = Array.from(new Set(selected.filter((id) => Number.isFinite(id))));
  const contentRoles = Array.from(
    new Set((options?.contentRoleIds ?? []).filter((id) => Number.isFinite(id)))
  );

  const temporalId = options?.temporalTagId;
  const temporal =
    temporalId != null && Number.isFinite(temporalId) ? [temporalId] : [];

  return Array.from(
    new Set([...preserved, ...uniqueSelected, ...temporal, ...contentRoles])
  );
}

/** Prefer `tag_ids`; fall back to legacy `tags` array on some payloads. */
export function assetTagIds(asset: {
  tag_ids?: number[] | null;
  tags?: number[] | null;
}): number[] {
  return asset.tag_ids ?? asset.tags ?? [];
}

/** Content-role ids present on the asset from the board content-role catalog. */
export function contentRoleIdsFromAsset(
  existingIds: number[],
  contentRoleTagIds: number[]
): number[] {
  const roleSet = new Set(contentRoleTagIds);
  return existingIds.filter((id) => roleSet.has(id));
}

/**
 * Prefill bulk-accept fields from the current selection.
 * - 1 asset: that asset's solution + temporal + content-role tags
 * - many: intersection of solution / content-role tags; shared temporal only if every asset agrees
 */
export function initialAcceptFieldsFromAssets(
  assets: { tag_ids?: number[] | null; tags?: number[] | null }[],
  solutionTags: SurfaceSolutionTag[],
  temporalTagIds: number[],
  contentRoleTagIds: number[] = []
): {
  solutionTagIds: number[];
  temporalTagId: number | null;
  contentRoleIds: number[];
} {
  if (!assets.length) {
    return {
      solutionTagIds: [],
      temporalTagId: null,
      contentRoleIds: [],
    };
  }

  const solutionIdSet = solutionTagIdSet(solutionTags);
  const temporalIdSet = new Set(temporalTagIds);
  const contentRoleIdSet = new Set(contentRoleTagIds);

  const perAsset = assets.map((asset) => {
    const ids = assetTagIds(asset);
    return {
      solutions: ids.filter((id) => solutionIdSet.has(id)),
      temporal: ids.find((id) => temporalIdSet.has(id)) ?? null,
      contentRoles: ids.filter((id) => contentRoleIdSet.has(id)),
    };
  });

  let solutionTagIds = perAsset[0].solutions;
  let contentRoleIds = perAsset[0].contentRoles;
  for (let i = 1; i < perAsset.length; i++) {
    const nextSolutions = new Set(perAsset[i].solutions);
    const nextRoles = new Set(perAsset[i].contentRoles);
    solutionTagIds = solutionTagIds.filter((id) => nextSolutions.has(id));
    contentRoleIds = contentRoleIds.filter((id) => nextRoles.has(id));
  }

  const firstTemporal = perAsset[0].temporal;
  // Unanimous (including all-null) keeps that value; mixed phases require an explicit pick.
  const temporalTagId = perAsset.every((a) => a.temporal === firstTemporal)
    ? firstTemporal
    : null;

  return { solutionTagIds, temporalTagId, contentRoleIds };
}

/** Shared accept-form gates for overlay + bulk sheet. */
export function getAcceptReadiness(input: {
  solutionTagIds: number[];
  temporalTagId: number | null;
  temporalTagCount: number;
}): { needsSolutionTag: boolean; needsTemporalTag: boolean; canAccept: boolean } {
  const needsSolutionTag = input.solutionTagIds.length < 1;
  const needsTemporalTag = input.temporalTagCount > 0 && input.temporalTagId == null;
  return {
    needsSolutionTag,
    needsTemporalTag,
    canAccept: !needsSolutionTag && !needsTemporalTag,
  };
}
